import { Component } from '@theme/component';

/**
 * The attachments field for `sections/partner-form.liquid`.
 *
 * The design draws its own control over a hidden `<input type="file">`, which means the browser's
 * own "3 files selected" text never appears — so choosing a file would otherwise give no feedback
 * at all. This names what was picked and marks the label as answered so the CSS can darken it.
 *
 * It does not submit, validate or upload anything; see the note at the top of the section.
 *
 * @typedef {{ file?: HTMLInputElement, fileName?: HTMLElement }} PartnerFormRefs
 * @extends Component<PartnerFormRefs>
 */
class PartnerFormComponent extends Component {
  /* No `requiredRefs`: clearing the attachments label in the editor removes the input and its
     label from the section, and a missing required ref throws MissingRefError out of
     connectedCallback (assets/component.js:135-141) — one emptied text setting would take the
     whole component down. Every use below guards instead. */

  /** The label to fall back to when the selection is cleared. */
  #restingLabel = '';

  connectedCallback() {
    super.connectedCallback();
    this.#restingLabel = this.refs.fileName?.textContent?.trim() ?? '';
  }

  /** Bound from the input's `on:change` attribute. */
  updateFiles() {
    const { file, fileName } = this.refs;

    if (!file || !fileName) return;

    const names = Array.from(file.files ?? []).map((entry) => entry.name);

    fileName.textContent = names.length ? names.join(', ') : this.#restingLabel;
    fileName.toggleAttribute('data-filled', names.length > 0);
  }
}

if (!customElements.get('partner-form-component')) {
  customElements.define('partner-form-component', PartnerFormComponent);
}
